<template>
  <div class="ayuda-global">
    <f7-button
      small
      class="display-flex"
      :icon-f7="ayuda ? 'question_circle_fill' : 'question_circle'"
      v-tooltip="ayuda ? ('Ayuda: ' + ayuda.titulo) : 'Ayuda'"
      @click="abrir"
    ></f7-button>

    <f7-popup class="popup-ayuda-pantalla" v-if="popupOpened" :opened="true" @popup:closed="cerrar">
      <f7-page>
        <f7-navbar>
          <f7-nav-title>{{ ayuda ? ayuda.titulo : 'Ayuda' }}</f7-nav-title>
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>

        <r-ayuda-contenido v-if="ayuda" :ayuda="ayuda"></r-ayuda-contenido>

        <div v-else class="ayuda-soporte">
          <p v-if="consulta && consulta.ruta">
            Todavía no hay instrucciones para la página con ruta <code class="ayuda-ruta">{{ consulta.ruta }}</code>.
          </p>
          <p v-else-if="consulta && consulta.clave">
            Todavía no hay instrucciones para la ayuda con clave <code class="ayuda-ruta">{{ consulta.clave }}</code>.
          </p>
          <p v-else>Esta pantalla todavía no tiene instrucciones.</p>

          <p v-if="esAdmin && consulta" class="ayuda-pista">
            Puedes crearlas en <strong>Ajustes → Ayudas de pantallas</strong>, dando de alta esa misma ruta.
          </p>

          <p>
            Si necesita ayuda o tiene cualquier problema con el uso de la aplicación puede contactar con:
            <br />
            <br />
            <f7-link href="mailto:soporte@simecal.com" class="justify-left" external>soporte@simecal.com</f7-link>
          </p>
        </div>

        <div class="ayuda-footer">
          <label v-if="ayuda" class="ayuda-nomostrar">
            <input type="checkbox" v-model="noMostrar" /> No volver a mostrar
          </label>
          <span v-else></span>
          <f7-button small fill popup-close>Entendido</f7-button>
        </div>
      </f7-page>
    </f7-popup>
  </div>
</template>

<script>
import rAyudaContenido from "./rAyudaContenido.vue";
import Ayuda from "../js/db/Ayuda.js";
import RoutesUtils from "../js/RoutesUtils.js";
import { claveDesdeRuta } from "../js/AyudaUtils.js";

var PREFIJO_VISTA = "ayuda_vista_";

/**
 * Icono de ayuda del appbar: es la única barra común a todas las pantallas (cada página declara su
 * propio f7-navbar), así que colgar aquí la ayuda la pone en toda la aplicación de una vez.
 *
 * Resuelve en cada navegación qué ayuda toca (por la ruta, o por la clave si la ruta la declara),
 * abre sola la que esté marcada como autoAbrir y, si la pantalla no tiene ayuda, cae al contacto
 * con soporte.
 */
export default {
  name: "rAyudaGlobal",
  components: {
    rAyudaContenido
  },
  data: function () {
    return {
      ayuda: null,
      // Con qué se preguntó por última vez ({ruta} o {clave}): se enseña cuando no hay ayuda, para
      // que se vea qué se buscó y con qué ruta hay que darla de alta.
      consulta: null,
      popupOpened: false,
      noMostrar: false
    };
  },
  computed: {
    esAdmin: function () {
      return RoutesUtils.checkRolesPermission(["ADMIN", "DTYCAL"]);
    }
  },
  mounted: function () {
    var self = this;
    self.$f7ready(function (f7) {
      self._onPageAfterIn = function (page) {
        if (!page || !page.view || page.view !== f7.views.main) return;
        self.resolverAyuda(page.route, true);
      };
      f7.on("pageAfterIn", self._onPageAfterIn);
    });
  },
  beforeDestroy: function () {
    if (this._onPageAfterIn && this.$f7) this.$f7.off("pageAfterIn", this._onPageAfterIn);
  },
  methods: {
    /** @param {boolean} permitirAutoAbrir sólo al navegar, no al pulsar el icono. */
    resolverAyuda: function (route, permitirAutoAbrir) {
      var self = this;
      // Sin sesión (login, 403…) no se pregunta: un 401 aquí cerraría la sesión del usuario.
      if (!RoutesUtils.checkAuth()) {
        self.olvidarPeticion();
        return Promise.resolve(null);
      }
      var params = claveDesdeRuta(route);
      if (!params.clave && !params.ruta) {
        self.olvidarPeticion();
        return Promise.resolve(null);
      }
      self.consulta = params;
      // Se navega más rápido de lo que responde el servidor: si al volver la respuesta ya estamos
      // en otra pantalla, se tira. Si no, la ayuda de la pantalla anterior se quedaría puesta —y
      // hasta se abriría sola encima de la nueva— y dos respuestas cruzadas dejarían el icono
      // hablando de una pantalla que ya no se está viendo.
      var token = self.tokenDe(params);
      if (self._peticionActual !== token) {
        // Al cambiar de pantalla la ayuda anterior se retira YA, sin esperar a la respuesta: si no,
        // durante ese hueco el icono sigue encendido y `abrir` enseñaría las instrucciones de la
        // pantalla de la que se acaba de salir.
        self.ayuda = null;
        self._peticionActual = token;
      }
      return Ayuda.resolver(params)
        .then(function (ayuda) {
          if (self._peticionActual !== token) return null;
          self.ayuda = ayuda || null;
          if (permitirAutoAbrir && self.ayuda && self.ayuda.autoAbrir && self.pendienteDeVer(self.ayuda)) {
            self.abrirPopup();
          }
          return self.ayuda;
        })
        .catch(function (e) {
          console.error("[ayuda] No se pudo resolver la ayuda de la pantalla", e);
          if (self._peticionActual === token) self.ayuda = null;
          return null;
        });
    },
    /** Deja la pantalla sin ayuda y descarta lo que esté en vuelo. */
    olvidarPeticion: function () {
      this._peticionActual = null;
      this.ayuda = null;
      this.consulta = null;
    },
    abrir: function () {
      var self = this;
      // A demanda se abre siempre, aunque esté marcada como "no volver a mostrar".
      if (self.ayuda) {
        self.abrirPopup();
        return;
      }
      var route = self.$f7.views.main ? self.$f7.views.main.router.currentRoute : null;
      var rutaAlPulsar = self.rutaDe(route);
      self.resolverAyuda(route, false).then(function () {
        // Si mientras se resolvía se ha cambiado de pantalla, el popup ya no lo ha pedido nadie:
        // aparecería solo en una pantalla donde no se ha pulsado nada.
        if (self.rutaDe(self.$f7.views.main ? self.$f7.views.main.router.currentRoute : null) !== rutaAlPulsar) return;
        self.abrirPopup();
      });
    },
    /** Identifica de forma estable lo que se pregunta, para poder comparar peticiones. */
    tokenDe: function (params) {
      if (!params) return null;
      return params.clave ? "c:" + params.clave : (params.ruta ? "r:" + params.ruta : null);
    },
    rutaDe: function (route) {
      return this.tokenDe(claveDesdeRuta(route));
    },
    abrirPopup: function () {
      this.noMostrar = this.ayuda ? !this.pendienteDeVer(this.ayuda) : false;
      this.popupOpened = true;
    },
    cerrar: function () {
      var self = this;
      if (self.ayuda) {
        try {
          if (self.noMostrar) localStorage.setItem(PREFIJO_VISTA + self.ayuda.clave, self.selloVersion(self.ayuda));
          else localStorage.removeItem(PREFIJO_VISTA + self.ayuda.clave);
        } catch (e) {
          /* sin localStorage no se recuerda, pero no debe romper */
        }
      }
      self.popupOpened = false;
      // F7 deja el backdrop/with-modal-popup al desmontar con v-if; lo limpiamos si no queda popup.
      setTimeout(function () {
        // slice.call en vez de NodeList.forEach: los WebView antiguos de Android no lo traen.
        var backdrops = Array.prototype.slice.call(document.querySelectorAll(".popup-backdrop.backdrop-in"));
        backdrops.forEach(function (el) {
          el.classList.remove("backdrop-in");
        });
        if (!document.querySelector(".popup.modal-in")) {
          document.documentElement.classList.remove(
            "with-modal-popup",
            "with-modal-popup-push",
            "with-modal-popup-push-closing"
          );
        }
      }, 50);
    },
    /**
     * Se guarda la versión vista, no un booleano: al editar la ayuda cambia modificationDate y la
     * vuelve a ver todo el mundo aunque hubiera marcado "no volver a mostrar".
     */
    pendienteDeVer: function (ayuda) {
      try {
        return localStorage.getItem(PREFIJO_VISTA + ayuda.clave) !== this.selloVersion(ayuda);
      } catch (e) {
        return true;
      }
    },
    selloVersion: function (ayuda) {
      return String(ayuda.modificationDate || ayuda.creationDate || "1");
    }
  }
};
</script>

<style scoped>
.ayuda-global {
  display: inline-flex;
  align-items: center;
}
.ayuda-soporte {
  padding: 14px 16px 8px;
  font-size: 14px;
}
.ayuda-ruta {
  padding: 1px 6px;
  background: #f3eefc;
  border: 1px solid #d8c9f5;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  color: #4a148c;
  word-break: break-all;
}
.ayuda-pista {
  font-size: 13px;
  color: #777;
}
.ayuda-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #eef1f5;
}
.ayuda-nomostrar {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
}
</style>
